import { useCallback, useState, useEffect, useMemo } from 'react'
import { VersionReportTemplateEntity } from '../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { useVersionReportTemplateRepository } from '../../../../services/adaptors/versionReportTemplateRepositoryAdaptor'

export type UseTemplateDataReturnValue = ReturnType<typeof useTemplateData>

export const useTemplateData = () => {
  const [templates, setTemplates] = useState<VersionReportTemplateEntity[]>([])
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<
    string | undefined
  >()
  const [initialized, setInitialized] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const { fetchAll } = useVersionReportTemplateRepository()

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateUuid) return undefined
    return templates.find(template => template.uuid === selectedTemplateUuid)
  }, [selectedTemplateUuid, templates])

  const fetchTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetchAll()
      setTemplates(response)
      return response
    } catch (error) {
      console.error('Failed to fetch templates:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [fetchAll])

  const selectTemplate = useCallback(
    (template: VersionReportTemplateEntity) => {
      setSelectedTemplateUuid(template.uuid)
    },
    []
  )

  const selectTemplateByUuid = useCallback((uuid: string | undefined) => {
    setSelectedTemplateUuid(uuid)
  }, [])

  const resetTemplateSelection = useCallback(() => {
    setSelectedTemplateUuid(undefined)
  }, [])

  const refreshTemplates = useCallback(async () => {
    await fetchTemplates()
  }, [fetchTemplates])

  useEffect(() => {
    if (initialized) return
    const fn = async () => {
      await fetchTemplates()
      setInitialized(true)
    }
    fn()
  }, [fetchTemplates, initialized])

  return {
    templates,
    selectedTemplate,
    loading,
    selectTemplate,
    selectTemplateByUuid,
    resetTemplateSelection,
    refreshTemplates,
  }
}
